import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { store } from 'src/common/redux/configure-store';

const getRegexExpWithPrecision = (): RegExp => {
  const precision = store.getState().touchPoint.touchPointConfiguration
    .defaultWeightUnitOfMeasurePrecision;
  const regExpression = `\\d+[.,]?\\d{0,${precision}}`;
  return new RegExp(regExpression);
};

// Default formatter rifm: only digits and a period
export const defaultRifmOptions = (value: string, setValue): RifmArguments => {
  return {
    accept: /[\d.]+/g,
    format: string => (string.match(getRegexExpWithPrecision()) || []).join(''),
    value: value,
    onChange: e => {
      setValue(e);
    },
  };
};

// Price formatter rifm: only whole numbers or decimal numbers up to the hundredth
export const priceRifmOptions = (value: string, setValue, ref?): RifmArguments => {
  return {
    accept: /[\d.]+/g,
    format: data => priceFormat(data, ref, setValue),
    value: value,
    onChange: e => setValue(e),
  };
};

export const noDecimalRifmOptions = (value: string, setValue): RifmArguments => {
  return {
    accept: /[\d.]+/g,
    format: string => (string.match(/\d+[,]?\d*/) || []).join(''),
    value: value,
    onChange: e => {
      setValue(e);
    },
  };
};

// Date formatter rifm
const appendSlash = v => (v.length === 2 || v.length === 5 ? `${v}/` : v);

export const priceFormat = (priceInput: string, inputRef, setValue?) => {
  if (inputRef) {
    setTimeout(() => {
      const length = inputRef.current?.value?.length ?? 0;
      inputRef.current?.setSelectionRange(length, length);
    }, 0);
  }

  let result = priceInput;

  if (priceInput.length >= 1) {
    if (priceInput.includes('.')) {
      const newValue = priceInput.replace('.', '');
      result = `${(getValueAsNumber(getValueAsNumber(newValue).toFixed(2)) * 0.01).toFixed(2)}`;
      if (result !== priceInput) {
        setValue && setValue(result);
      }
    } else {
      result = `${priceInput}.00`;
      if (result !== priceInput) {
        setValue && setValue(result);
      }
    }
  } else {
    result = '0.00';
    if (result !== priceInput) {
      setValue && setValue(result);
    }
  }

  return result;
};

const dateFormat = string => {
  const res = formatDate(string);

  if (string.endsWith('/')) {
    if (res.length === 2) {
      return `${res}/`;
    }

    if (res.length === 5) {
      return `${res}/`;
    }
  }
  return res;
};

const formatDate = string => {
  const digits = parseDigits(string);
  const chars = digits.split('');
  return chars
    .reduce((r, v, index) => (index === 2 || index === 4 ? `${r}/${v}` : `${r}${v}`), '')
    .substr(0, 10);
};

const parseDigits = string => (string.match(/\d+/g) || []).join('');

export const dateRifmOptions = (value: string, setValue): RifmArguments => {
  return {
    accept: /\d+/g,
    mask: 10 <= value.length,
    format: dateFormat,
    append: appendSlash,
    value: value,
    onChange: e => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setValue(e);
        });
      });
    },
  };
};

export const RifmOptions = {
  date: dateRifmOptions,
  default: defaultRifmOptions,
  price: priceRifmOptions,
  noDecimal: noDecimalRifmOptions,
};

export interface RifmArguments {
  accept?: RegExp;
  format: (string) => string;
  value: string;
  onChange: (string) => void;
  mask?: boolean;
  replace?: (string) => string;
  append?: (string) => string;
}
